import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { storage } from '@doxyme/ui/utils';
import { StorageKeys } from '../../shared/constants';

const initialState: {
  isGalleryView: boolean;
  isSidebarOpen: boolean;
  isNoiseSuppressionEnabled: boolean;
  isSpeakingWhileMutedSeen: boolean;
  /**
   * The participant that is currently on the main screen in the LayoutManager. Pinned or active.
   * Could be set up only in Speaker Layout as in GalleryView there is no concept of main screen.
   */
  mainCallParticipantId?: string;
  /**
   * To be displayed in the PIP video when enabled.
   */
  pipCallParticipantId?: string;
} = {
  isGalleryView:
    storage.getItem<boolean>(StorageKeys.GroupCallGalleryView, true) ?? true,
  isSidebarOpen: false,
  isNoiseSuppressionEnabled: false,
  isSpeakingWhileMutedSeen: false,
  mainCallParticipantId: undefined,
  pipCallParticipantId: undefined,
};

/**
 * A Redux slice that manages the state of a call.
 * It includes two actions (`setTransition` and `deprecatedSetCallState`), which trigger
 * state transitions in the state machine.
 *
 * To decide if something is a call-wide state, answer the question:
 * - "Does this state need to be shared among participants?"
 * - "Does it affect the actions that can be taken during the call?"
 * If the answer is yes to one or more of these questions, then it's a call-wide state.
 */
export const callSlice = createSlice({
  name: 'callState',
  initialState,
  reducers: {
    /**
     * Triggers a state transition in the state machine.
     * @param state - The current state of the slice.
     * @param action - The dispatched action.
     */

    setIsGalleryView: (state, action: PayloadAction<boolean>) => {
      state.isGalleryView = action.payload;
    },

    setIsSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.isSidebarOpen = action.payload;
    },

    setIsNoiseSuppressionEnabled: (state, action: PayloadAction<boolean>) => {
      state.isNoiseSuppressionEnabled = action.payload;
    },

    setIsSpeakingWhileMutedSeen: (state, action: PayloadAction<boolean>) => {
      state.isSpeakingWhileMutedSeen = action.payload;
    },

    setMainCallParticipantId: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      state.mainCallParticipantId = action.payload;
    },

    setPipCallParticipantId: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      state.pipCallParticipantId = action.payload;
    },
  },
});

export const {
  setIsGalleryView,
  setIsSidebarOpen,
  setIsNoiseSuppressionEnabled,
  setIsSpeakingWhileMutedSeen,
  setMainCallParticipantId,
  setPipCallParticipantId,
} = callSlice.actions;
